'use client';

import { useState } from 'react';
import { useIntl } from 'react-intl';
import type { FragmentType } from '@packages/gql/generated/shopping';
import type { SelectValuesFragmentFragmentDoc } from '@packages/gql/generated/shopping/SelectValuesFragmentFragmentDoc';
import { unmask } from '@packages/gql/src/betterMasking';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { VariationSelectionDialog } from '../VariationSelectionDialog/VariationSelectionDialog.client';
import { VariationIcon } from '../VariationIcon/VariationIcon';
import type { VariationSelectionOnSelectHandler } from '../types';

/* GraphQL */ `
  fragment SelectValuesFragment on ProductDimension {
    displayName
    values {
      displayText
      image
      variationMainImage
      isSelected
      rgb
      sku
      availability {
        state
        message
      }
    }
  }
`;

export type SelectDimensionProps = VariationSelectionOnSelectHandler & {
  /**
   * The GraphQL dimension data for the given product
   */
  data: FragmentType<typeof SelectValuesFragmentFragmentDoc>;
};

/**
 * SelectDimension component
 *
 * used to display the selected dimension
 * opens dialog on click to select a different variation
 *
 * */
export const SelectDimension = ({ data, onSelect }: SelectDimensionProps) => {
  const { formatMessage } = useIntl();

  const [open, setOpen] = useState(false);

  const unmaskedData = unmask<typeof SelectValuesFragmentFragmentDoc>(data);

  const selectedVariation = unmaskedData.values.find((variation) => variation.isSelected === true);

  const isDisabled = unmaskedData.values.length === 1;

  return (
    <>
      <Button
        className={`w-full ${isDisabled ? 'border-color-grey bg-color-background-base opacity-100!' : ''}`}
        disabled={isDisabled}
        onClick={() => setOpen(true)}
      >
        <span className="flex w-full flex-row flex-nowrap items-center justify-between gap-1.5 py-0.5">
          {selectedVariation &&
            (selectedVariation.rgb ||
              selectedVariation.image ||
              selectedVariation.variationMainImage) && (
              <VariationIcon mode="BUTTON" data={selectedVariation} />
            )}
          <span
            className={`text-size-body2 shrink grow basis-full text-left ${isDisabled ? 'text-color-text-darktransparent' : ''}`}
          >
            {selectedVariation !== undefined
              ? selectedVariation.displayText
              : formatMessage({
                  id: 'product.variationSelect.select.selectVariation',
                  defaultMessage: 'Bitte wählen',
                })}
          </span>
          {/* if disabled render a placeholder "icon" to reserve height and space to be equal to the enabled version */}
          <span
            className={`size-4 shrink-0 grow-0 basis-4 ${isDisabled ? '' : 'icon-emp-arrow-mini-right'}`}
          />
        </span>
      </Button>
      {open && (
        <VariationSelectionDialog
          data={unmaskedData}
          open={open}
          onClose={() => setOpen(false)}
          onSelect={onSelect}
        />
      )}
    </>
  );
};
